// src/components/ProductCard/ProductCard.tsx

import React from 'react';
import Typography from '../Text/Typography';
import IconButton from '../Button/IconButton';
import Icon from '../Icon/Icon';
import Tag from '../Tag/Tag';
import Colors from '../Colors/Colors';
import ContentfulImage from '../ContentfulImage/ContentfulImage';
import type { ProductDetailProduct } from '../ProductDetailContent/ProductDetailContent';
import './ProductCard.css';

interface ProductCardProps {
  imageSrc?: string;
  company: string;
  productName: string;
  tag?: string;
  productId: number;
  onDelete?: (productId: number) => void;
  /** Full product for detail view; when provided with onClick, card is clickable */
  product?: ProductDetailProduct;
  onClick?: (product: ProductDetailProduct) => void;
}

const capitalizeWords = (str: string) => {
  return str.split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const ProductCard: React.FC<ProductCardProps> = ({
  imageSrc,
  company,
  productName,
  tag,
  productId,
  onDelete,
  product,
  onClick,
}) => {
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (onDelete) {
      onDelete(productId);
    }
  };

  const isClickable = Boolean(product && onClick);

  const cardContent = (
    <>
      {onDelete && (
        <IconButton
          icon={<Icon name="close" size="small" />}
          onClick={handleDelete}
          ariaLabel="Delete product"
          className="delete-button"
        />
      )}
      {imageSrc ? (
        <ContentfulImage
          src={imageSrc}
          alt={productName}
          className="product-card-image"
          width={200}
          height={200}
          fit="fill"
          quality={80}
          format="webp"
          responsive={true}
        />
      ) : (
        <div className="product-image-placeholder" />
      )}
      <div className="product-card-bottom">
        <div className="product-card-content">
          <Colors variant="dark-gray">
            <Typography variant="body-small-bold">
              {company}
            </Typography>
          </Colors>
          <Typography variant="body-bold">
            {productName}
          </Typography>
        </div>
        {tag && <Tag>{capitalizeWords(tag)}</Tag>}
      </div>
    </>
  );

  if (isClickable && product) {
    const handleCardClick = () => onClick(product);
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick(product);
      }
    };
    return (
      <div
        role="button"
        tabIndex={0}
        className="product-card product-card-clickable"
        onClick={handleCardClick}
        onKeyDown={handleKeyDown}
        aria-label={`View details for ${productName}`}
      >
        {cardContent}
      </div>
    );
  }

  return (
    <div className="product-card">
      {cardContent}
    </div>
  );
};

export default ProductCard;
